import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { ApiError, documentation, ForbiddenError, NotFoundError } from '@cwos/api-client';
import { ConsequenceNote, Pill, SkeletonSet } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import {
  ageLabel,
  groupSessionLabel,
  reviewerActions,
  stateLabel,
  statePillVariant,
  type ReviewerAction,
} from './documentationLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Documentation record (doc 01 A5.2 row → Evidence Drawer, promoted to its own
 * page for URL-as-truth).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * IT WAS READ-ONLY, AND THAT MADE A SHIPPED KPI A CONSTANT.
 *
 * This screen used to say, in its own docblock, "there is nothing to save
 * here — and therefore no save button that pretends otherwise". The first half
 * was false. The whole REVIEWER half of the documentation authority model —
 * mark late, clear the verdict, void the obligation — is served by
 * `POST /services/events/:id/documentation/transition` and its PUT sibling,
 * both bound in `packages/api-client/src/documentation.ts`, and no screen in
 * this app called either. So `is_late` was a stored column nobody could write,
 * `docs_overdue` (`is_late` counted, nothing more) read a permanent zero that
 * an organisation reads as a clean compliance record, and the queue's own
 * "Late only" / "Late" / "Void" pills could only ever come back empty.
 *
 * The reviewer panel below is that half. It is gated, its moves are derived
 * from the server's own transition table rather than retyped, the one
 * irreversible move confirms first, and its two refusals — you may not judge
 * this record, and this record is not yours to reach — are rendered as
 * refusals rather than as failures.
 */
export function DocumentationDetail() {
  const navigate = useNav();
  const { serviceEventId } = useParams({ strict: false }) as { serviceEventId: string };

  const record = useQuery({
    queryKey: ['documentation', 'detail', serviceEventId],
    queryFn: () => documentation.getDocumentation(serviceEventId),
  });

  return (
    <div className="rn-col" style={{ maxWidth: 720 }}>
      <button type="button" className="rn-link" onClick={() => navigate('/documentation')}>
        ← Documentation
      </button>

      {record.isLoading ? (
        <SkeletonSet surface="drawer" rows={8} />
      ) : record.isError && !record.data ? (
        <LoadFailure
          error={record.error}
          message="We couldn’t load this record just now."
          onRetry={() => void record.refetch()}
          mainRead
        />
      ) : record.data ? (
        <RecordView
          data={record.data}
          serviceEventId={serviceEventId}
          onBack={() => navigate('/documentation')}
        />
      ) : null}
    </div>
  );
}

function RecordView({
  data,
  serviceEventId,
  onBack,
}: {
  data: documentation.DocumentationDetail;
  serviceEventId: string;
  onBack: () => void;
}) {
  const when = new Date(data.occurredAt);
  const facts: Array<{ label: string; value: string }> = [
    { label: 'Client', value: data.clientInitials },
    {
      label: 'Worker',
      value:
        data.workerDisplayName ??
        (data.workerIds.length > 1 ? groupSessionLabel(data.workerIds.length) : 'Unassigned'),
    },
    { label: 'Service type', value: data.serviceTypeName ?? '—' },
    { label: 'Program', value: data.programName ?? '—' },
    { label: 'Duration', value: data.minutes === null ? '—' : `${data.minutes} min` },
    {
      label: 'Attendance',
      value:
        data.attendanceStatus === 'unknown'
          ? 'Not classified by the source system'
          : data.attendanceStatus,
    },
  ];

  return (
    <>
      <div className="rn-card">
        <div className="rn-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="rn-card-title" style={{ marginBottom: 0 }}>
            Visit · {when.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
          </h1>
          <Pill variant={statePillVariant(data.state)} dot>
            {stateLabel(data.state)}
          </Pill>
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
          {ageLabel(data.occurredAt)}
          {data.isLate ? ' · flagged late' : ''}
          {data.lagDays !== null ? ` · outstanding ${data.lagDays}d` : ''}
        </p>

        <dl className="rn-col" style={{ gap: 'var(--space-8)', marginTop: 'var(--space-12)' }}>
          {facts.map((f) => (
            <div key={f.label} className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <dt className="rn-muted" style={{ minWidth: 120 }}>
                {f.label}
              </dt>
              <dd style={{ margin: 0 }}>{f.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="rn-card">
        <h2 className="rn-card-title" style={{ fontSize: '1rem' }}>
          Note
        </h2>
        {data.note ? (
          <p style={{ whiteSpace: 'pre-wrap' }}>{data.note}</p>
        ) : (
          <p className="rn-muted">
            No note recorded yet
            {data.state === 'undocumented' || data.state === 'draft'
              ? ' — the visit’s documentation is still owed.'
              : '.'}
          </p>
        )}
      </div>

      <div className="rn-card">
        <h2 className="rn-card-title" style={{ fontSize: '1rem' }}>
          Documentation status
        </h2>
        <dl className="rn-col" style={{ gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
          {[
            { label: 'Incomplete checklist items', value: String(data.incompleteCount) },
            {
              label: 'First seen open',
              value: data.firstObservedOpenAt
                ? new Date(data.firstObservedOpenAt).toLocaleString()
                : '—',
            },
            {
              label: 'Last observed',
              value: data.lastObservedAt ? new Date(data.lastObservedAt).toLocaleString() : '—',
            },
            {
              label: 'Completed',
              value: data.completedObservedAt
                ? new Date(data.completedObservedAt).toLocaleString()
                : '—',
            },
          ].map((f) => (
            <div key={f.label} className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <dt className="rn-muted" style={{ minWidth: 180 }}>
                {f.label}
              </dt>
              <dd style={{ margin: 0 }}>{f.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <ReviewerPanel data={data} serviceEventId={serviceEventId} />

      <button
        type="button"
        className="rn-link"
        onClick={onBack}
        style={{ alignSelf: 'flex-start' }}
      >
        ← Back to the queue
      </button>
    </>
  );
}

/**
 * What went wrong, in the three categories that mean different things.
 *
 * A 403 here is not a failure and must not be offered a retry: the surface
 * gate wants `service:manage` or `service:write`, and then the authority over
 * THIS record decides what the body may contain — a `service:manage` holder is
 * the AUTHOR, not the reviewer, of their own note, and the server says so in a
 * sentence worth passing through verbatim. A 404 is the tenant/scope answer,
 * and it is also what a `transition` on a visit with no status row produces.
 * Everything else is a genuine failure.
 */
type ReviewProblem = { kind: 'refused' | 'absent' | 'failed'; text: string };

export function reviewProblem(error: unknown): ReviewProblem {
  if (error instanceof ForbiddenError) {
    return {
      kind: 'refused',
      text:
        error.message !== ''
          ? error.message
          : 'You may not make this judgement about this record. It is withheld, not broken.',
    };
  }
  if (error instanceof NotFoundError) {
    return {
      kind: 'absent',
      text:
        'This visit is not one this account can act on, or it has no documentation status to ' +
        'move yet. Nothing was changed.',
    };
  }
  if (error instanceof ApiError && error.message !== '') {
    return { kind: 'failed', text: `${error.message} Nothing was changed.` };
  }
  return {
    kind: 'failed',
    text: 'That change was not recorded, so nothing about this note moved.',
  };
}

const PROBLEM_COLOR: Record<ReviewProblem['kind'], string> = {
  refused: 'var(--color-unsure-deep)',
  absent: 'var(--color-unsure-deep)',
  failed: 'var(--color-urgent-deep)',
};

/**
 * The reviewer's three verbs.
 *
 * GATED ON `service:manage`, which is the key the reviewer branch of both
 * write routes requires. `service:write` also reaches the surface, but only
 * ever as the note's own author, and every control here is a compliance
 * judgement a self-service author is refused — so offering them to a
 * `service:write` holder would be three buttons that can only 403.
 *
 * WHICH VERB IS SENT is decided by whether there is a status row to move.
 * `transition` records the move and emits `documentation.transitioned`, which
 * is what a review screen wants; it throws `NotFoundError` when no row exists.
 * A visit the list calls `undocumented` may be a coalesced absence, so that one
 * state goes through the PUT, which creates the row.
 */
function ReviewerPanel({
  data,
  serviceEventId,
}: {
  data: documentation.DocumentationDetail;
  serviceEventId: string;
}) {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const [confirming, setConfirming] = useState<ReviewerAction | null>(null);
  const [problem, setProblem] = useState<ReviewProblem | null>(null);

  const actions = reviewerActions(data.state);

  const review = useMutation({
    mutationFn: (action: ReviewerAction) => {
      const input: documentation.DocumentationStateInput = {
        state: action.state,
        ...(action.isLate !== undefined ? { isLate: action.isLate } : {}),
      };
      return data.state === 'undocumented'
        ? documentation.setState(serviceEventId, input)
        : documentation.transition(serviceEventId, input);
    },
    onSuccess: () => {
      setProblem(null);
      setConfirming(null);
      // Re-read rather than patch: the server owns the resulting state, the
      // lateness verdict and the timestamps, and a locally-guessed row is how
      // a screen comes to disagree with the queue it was opened from.
      void qc.invalidateQueries({ queryKey: ['documentation', 'detail', serviceEventId] });
      void qc.invalidateQueries({ queryKey: ['documentation', 'list'] });
    },
    onError: (error) => {
      setConfirming(null);
      setProblem(reviewProblem(error));
    },
  });

  if (!has('service:manage')) return null;

  // The busy label follows the action that is RUNNING, not the fact that
  // something is. Three buttons served by one mutation is how "Recording…"
  // ends up on the control the operator did not press.
  const running = review.isPending ? review.variables : undefined;

  return (
    <div className="rn-card">
      <h2 className="rn-card-title" style={{ fontSize: '1rem' }}>
        Review this note
      </h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
        Marking a note late is a compliance judgement about somebody else’s work, and it is what the
        overdue-documentation figure counts. Nothing computes it — if no one records it, that figure
        reads zero and means “nobody looked”, not “nothing was late”.
      </p>

      {actions.length === 0 ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)', marginBottom: 0 }}>
          This obligation was voided, which is the last state it has. There is nothing further to
          decide about it.
        </p>
      ) : confirming !== null ? (
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
          <ConsequenceNote text={confirming.consequence ?? ''} />
          <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="rn-link"
              disabled={review.isPending}
              onClick={() => review.mutate(confirming)}
            >
              {running?.kind === confirming.kind ? 'Voiding…' : 'Yes, void it'}
            </button>
            <button
              type="button"
              className="rn-link"
              disabled={review.isPending}
              onClick={() => setConfirming(null)}
            >
              Keep the obligation
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            marginTop: 'var(--space-12)',
            display: 'flex',
            gap: 'var(--space-8)',
            flexWrap: 'wrap',
          }}
        >
          {actions.map((action) => (
            <button
              key={action.kind}
              type="button"
              className="rn-link"
              disabled={review.isPending}
              onClick={() => {
                setProblem(null);
                if (action.irreversible) setConfirming(action);
                else review.mutate(action);
              }}
            >
              {running?.kind === action.kind ? 'Recording…' : action.label}
            </button>
          ))}
        </div>
      )}

      {problem ? (
        <p
          role="status"
          aria-live="polite"
          style={{
            marginTop: 'var(--space-12)',
            marginBottom: 0,
            color: PROBLEM_COLOR[problem.kind],
          }}
        >
          {problem.text}
        </p>
      ) : null}
    </div>
  );
}
